/**
 * Pure helpers for the student activity log (Phase 138).
 *
 * Everything here is deliberately free of React and of the Supabase client so
 * the date and grouping rules can be unit-tested directly.
 *
 * The money helpers that used to live here moved to `shared/lib/money.ts` in
 * Phase 153: billing needs them from `features/student` too, and a sibling
 * feature import is what the layering rule forbids.
 */

export const EVENT_CATEGORIES = [
  'enrolment',
  'finance',
  'class',
  'test',
  'communication',
  'document',
  'admin',
  'pipeline',
] as const;

export type EventCategory = (typeof EVENT_CATEGORIES)[number];

const CATEGORY_LABEL: Record<EventCategory, string> = {
  enrolment: 'Enrolment',
  finance: 'Fees & payments',
  class: 'Classes',
  test: 'Tests',
  communication: 'Contact',
  document: 'Documents',
  admin: 'Admin',
  pipeline: 'Applications',
};

/**
 * Accessor rather than an exported record, so the one lookup that
 * `security/detect-object-injection` cannot prove safe is written once here
 * instead of at each of the four call sites.
 */
export function categoryLabel(category: EventCategory): string {
  // eslint-disable-next-line security/detect-object-injection -- `category` is the EventCategory literal union, and CATEGORY_LABEL is a complete own-key record over it
  return CATEGORY_LABEL[category];
}

export const EVENT_COLORS = ['slate', 'teal', 'blue', 'green', 'amber', 'rose', 'violet'] as const;

export type EventColor = (typeof EVENT_COLORS)[number];

export function isEventCategory(value: string): value is EventCategory {
  return (EVENT_CATEGORIES as readonly string[]).includes(value);
}

export function isEventColor(value: string): value is EventColor {
  return (EVENT_COLORS as readonly string[]).includes(value);
}

/* ------------------------------------------------------------------- dates -- */

/**
 * `<input type="datetime-local">` speaks naive local time with no zone, so it
 * cannot take an ISO string with a `Z` or an offset. These two convert across
 * that boundary in the browser's own zone, which is what the person filling in
 * the form means by "3pm".
 */
export function toLocalInput(iso: string | null): string {
  if (iso === null || iso === '') return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return (
    `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

export function fromLocalInput(value: string): string | null {
  if (value === '') return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/**
 * The one-click date shortcuts on the composer (Phase 144).
 *
 * Typing into a `datetime-local` is six interactions for a date that is almost
 * always "now" or "yesterday", so those are chips and the full picker is the
 * fallback. Times are chosen to be plausible rather than midnight: an event
 * backdated to 00:00 sorts oddly against the rest of that day's entries.
 */
export const WHEN_PRESETS = ['now', 'today_morning', 'yesterday'] as const;
export type WhenPreset = (typeof WHEN_PRESETS)[number];

export function presetOccurredAt(preset: WhenPreset, now: Date = new Date()): string {
  const d = new Date(now.getTime());
  if (preset === 'today_morning') {
    d.setHours(9, 0, 0, 0);
  } else if (preset === 'yesterday') {
    d.setDate(d.getDate() - 1);
    d.setHours(9, 0, 0, 0);
  }
  return toLocalInput(d.toISOString());
}

/** Which chip, if any, the current field value corresponds to. */
export function matchWhenPreset(value: string, now: Date = new Date()): WhenPreset | null {
  if (value === '') return null;
  // "now" moves every minute, so it matches anything within the current minute
  // rather than by string equality.
  for (const preset of WHEN_PRESETS) {
    if (presetOccurredAt(preset, now) === value) return preset;
  }
  return null;
}

export type FollowUpState = 'none' | 'done' | 'overdue' | 'due_today' | 'upcoming';

export function followUpState(
  followUpAt: string | null,
  doneAt: string | null,
  now: Date = new Date(),
): FollowUpState {
  if (followUpAt === null) return 'none';
  if (doneAt !== null) return 'done';
  const due = new Date(followUpAt);
  if (Number.isNaN(due.getTime())) return 'none';
  if (due.getTime() < now.getTime()) return 'overdue';
  // "Due today" is calendar-day equality, not "within 24 hours": a task due at
  // 6pm is today's problem at 9am, and a task due tomorrow at 8am is not.
  const sameDay =
    due.getFullYear() === now.getFullYear() &&
    due.getMonth() === now.getMonth() &&
    due.getDate() === now.getDate();
  return sameDay ? 'due_today' : 'upcoming';
}

/* ------------------------------------------------------------- grouping -- */

export interface TimelineItemLike {
  id: string;
  occurred_at: string;
}

export interface TimelineGroup<T extends TimelineItemLike> {
  /** `YYYY-MM` — stable across renders and safe as a React key. */
  key: string;
  label: string;
  items: T[];
}

/**
 * Groups an already-sorted (newest first) list by calendar month.
 *
 * Month, not day: a consultancy logs a handful of things per student per month,
 * and day headers would outnumber the entries they head.
 */
export function groupByMonth<T extends TimelineItemLike>(items: T[]): TimelineGroup<T>[] {
  const out: TimelineGroup<T>[] = [];
  for (const item of items) {
    const d = new Date(item.occurred_at);
    if (Number.isNaN(d.getTime())) continue;
    const key = `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const last = out[out.length - 1];
    if (last !== undefined && last.key === key) {
      last.items.push(item);
    } else {
      out.push({
        key,
        label: d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
        items: [item],
      });
    }
  }
  return out;
}
